import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDaysYmd, athensYmd, fmtDM, isYmd } from '../../home/format';
import { GHOST_BTN, PRIMARY_BTN } from './ui';
import './listPage.css';

/**
 * Περίοδος στο ListToolbar: Όλες · Σήμερα · Χθες · 7 ημ. · 30 ημ. · Προσαρμογή… — ίδιο χειριστήριο με τον
 * επιλογέα της Αρχικής (home/PeriodPicker), ώστε η εφαρμογή να έχει ΕΝΑ τρόπο επιλογής ημερομηνιών. Τιμές
 * «YYYY-MM-DD» ή '' (χωρίς όριο), ημέρες Ελλάδας (ο server είναι UTC, η επιχείρηση όχι). Το `to` είναι η
 * τελευταία μέρα ΜΕΣΑ στο διάστημα («Σήμερα» = from και to σήμερα): αν ο server συγκρίνει με `<=` μια
 * σκέτη ημερομηνία, η σελίδα στέλνει το τέλος της μέρας (βλ. Κλήσεις).
 */

export type DatePreset = 'today' | 'yesterday' | 'last7' | 'last30';
export const DATE_PRESETS: DatePreset[] = ['today', 'yesterday', 'last7', 'last30'];

export interface DateRangeValue {
  from: string;
  to: string;
}

/** Η σημερινή μέρα στην Ελλάδα. */
export const todayYmd = (): string => athensYmd(new Date());

/** Το διάστημα ενός preset (π.χ. για αρχική τιμή «Σήμερα» σε μια σελίδα). */
export function presetRange(preset: DatePreset, today: string = todayYmd()): DateRangeValue {
  switch (preset) {
    case 'today': return { from: today, to: today };
    case 'yesterday': {
      const y = addDaysYmd(today, -1);
      return { from: y, to: y };
    }
    case 'last7': return { from: addDaysYmd(today, -6), to: today };
    default: return { from: addDaysYmd(today, -29), to: today };
  }
}

/** Ποιο κουμπί αντιστοιχεί σε ένα διάστημα: 'all' (χωρίς όρια), ένα preset, ή 'custom'. */
export function datePresetOf(range: DateRangeValue, presets: DatePreset[] = DATE_PRESETS, today: string = todayYmd()): DatePreset | 'all' | 'custom' {
  if (!range.from && !range.to) return 'all';
  const hit = presets.find(p => {
    const r = presetRange(p, today);
    return r.from === range.from && r.to === range.to;
  });
  return hit ?? 'custom';
}

export interface DateRangeProps {
  from: string;
  to: string;
  onChange: (range: DateRangeValue) => void;
  /** Ποια έτοιμα διαστήματα (προεπιλογή: σήμερα, χθες, 7 και 30 ημέρες). */
  presets?: DatePreset[];
  /** Κουμπί «Όλες» (χωρίς φίλτρο ημερομηνίας). Προεπιλογή true. */
  allowAll?: boolean;
  /** Τελευταία επιτρεπτή μέρα (προεπιλογή σήμερα· null = χωρίς όριο, π.χ. κρατήσεις). */
  max?: string | null;
  /** Όνομα ομάδας για αναγνώστες οθόνης (προεπιλογή «Περίοδος»). */
  label?: string;
  testId?: string;
}

type Key = DatePreset | 'all' | 'custom';

export const DateRange: React.FC<DateRangeProps> = ({
  from, to, onChange, presets = DATE_PRESETS, allowAll = true, max, label, testId = 'date-range',
}) => {
  const { t } = useTranslation(['listPage']);
  const uid = useId();
  const today = todayYmd();
  const limit = max === undefined ? today : max;
  const active = datePresetOf({ from, to }, presets, today);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRangeValue>({ from, to });
  const boxRef = useRef<HTMLDivElement>(null);
  const customRef = useRef<HTMLButtonElement>(null);
  const fromRef = useRef<HTMLInputElement>(null);

  // Κλικ έξω κλείνει το παράθυρο (όπως στην Αρχική). Το Esc το πιάνει το ίδιο το παράθυρο (onKeyDown):
  // έτσι, μέσα σε συρτάρι, το Esc κλείνει μόνο το παράθυρο ημερομηνιών και όχι και το συρτάρι.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    fromRef.current?.focus();
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const keys: Key[] = [...(allowAll ? ['all' as const] : []), ...presets, 'custom'];

  const labelOf = (key: Key): string => {
    switch (key) {
      case 'all': return t('listPage:dateRange.all');
      case 'today': return t('listPage:dateRange.today');
      case 'yesterday': return t('listPage:dateRange.yesterday');
      case 'last7': return t('listPage:dateRange.last7');
      case 'last30': return t('listPage:dateRange.last30');
      default:
        if (active !== 'custom') return t('listPage:dateRange.custom');
        if (from && to) return from === to ? fmtDM(from) : `${fmtDM(from)} – ${fmtDM(to)}`;
        return from ? t('listPage:dateRange.fromOnly', { date: fmtDM(from) }) : t('listPage:dateRange.toOnly', { date: fmtDM(to) });
    }
  };

  const pick = (key: Key) => {
    if (key === 'custom') {
      if (!open) setDraft(from || to ? { from, to } : { from: today, to: today });
      setOpen(o => !o);
      return;
    }
    setOpen(false);
    onChange(key === 'all' ? { from: '', to: '' } : presetRange(key, today));
  };

  const close = () => {
    setOpen(false);
    customRef.current?.focus();
  };

  const okDay = (d: string) => d === '' || (isYmd(d) && (!limit || d <= limit));
  const valid = (draft.from !== '' || draft.to !== '') && okDay(draft.from) && okDay(draft.to)
    && (!draft.from || !draft.to || draft.from <= draft.to);

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onChange({ from: draft.from, to: draft.to });
    close();
  };

  const groupLabel = label || t('listPage:dateRange.label');

  return (
    <div className="lp-period" ref={boxRef} data-testid={testId}>
      <div className="lp-seg" role="radiogroup" aria-label={groupLabel}>
        {keys.map(key => (
          <button
            key={key}
            ref={key === 'custom' ? customRef : undefined}
            type="button"
            role="radio"
            aria-checked={active === key}
            aria-expanded={key === 'custom' ? open : undefined}
            aria-controls={key === 'custom' && open ? `${uid}-custom` : undefined}
            onClick={() => pick(key)}
            data-testid={`${testId}-${key}`}
          >
            {labelOf(key)}
          </button>
        ))}
      </div>
      {open && (
        <form
          id={`${uid}-custom`}
          className="lp-pop"
          role="dialog"
          aria-label={t('listPage:dateRange.customTitle')}
          onSubmit={apply}
          onKeyDown={e => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            close();
          }}
        >
          <div className="lp-pop-row">
            <label className="lp-pop-field">
              <span>{t('listPage:dateRange.from')}</span>
              <input ref={fromRef} type="date" className="lp-date" value={draft.from} max={draft.to || limit || undefined}
                onChange={e => setDraft(d => ({ ...d, from: e.target.value }))} data-testid={`${testId}-from`} />
            </label>
            <label className="lp-pop-field">
              <span>{t('listPage:dateRange.to')}</span>
              <input type="date" className="lp-date" value={draft.to} min={draft.from || undefined} max={limit || undefined}
                onChange={e => setDraft(d => ({ ...d, to: e.target.value }))} data-testid={`${testId}-to`} />
            </label>
          </div>
          {!valid && (draft.from !== '' || draft.to !== '') && (
            <p className="lp-pop-error" role="alert">{t('listPage:dateRange.invalid')}</p>
          )}
          <div className="lp-pop-actions">
            <button type="button" className={GHOST_BTN} onClick={close}>{t('listPage:dateRange.cancel')}</button>
            <button type="submit" className={PRIMARY_BTN} disabled={!valid} data-testid={`${testId}-apply`}>
              {t('listPage:dateRange.apply')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default DateRange;
